import { useState } from 'react';

const RecruitmentSection = () => {
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    ville: '',
    experience: '',
    specialite: '',
    chiffreAffaires: '',
    disponibilites: '',
    motivations: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4tkc01t85rh3e48p810', {
        method: 'POST',
        body: new URLSearchParams(formDataToSend as any)
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          nom: '', prenom: '', email: '', telephone: '', ville: '',
          experience: '', specialite: '', chiffreAffaires: '', 
          disponibilites: '', motivations: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitStatus('idle'), 5000);
    }
  };

  return (
    <section className="py-20 bg-gradient-to-br from-blue-50 to-teal-50">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* En-tête principal */}
        <div className="text-center mb-16">
          <div className="inline-block bg-gradient-to-r from-blue-600 to-teal-600 text-white px-6 py-2 rounded-full text-sm font-semibold mb-4">
            Rejoignez-Nous
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Devenez Partenaire Expert
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Auto-entrepreneur en menuiserie, fenêtres ou volets ? Développez votre activité avec nos leads qualifiés
          </p>
        </div>

        {/* Grille principale : Informations + Formulaire */}
        <div className="grid lg:grid-cols-2 gap-12 mb-12">
          
          {/* Colonne gauche : Avantages et informations */}
          <div className="space-y-6">
            {/* Avantages clés */}
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                <i className="ri-star-line text-blue-600 mr-3"></i>
                Pourquoi Nous Rejoindre ?
              </h3>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-money-euro-circle-line text-2xl text-blue-600"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Leads Qualifiés</h4>
                    <p className="text-sm text-gray-600">Clients pré-qualifiés avec projets confirmés et budgets définis</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-2xl text-teal-600"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Proximité Géographique</h4>
                    <p className="text-sm text-gray-600">Leads dans votre zone pour optimiser vos déplacements</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-line-chart-line text-2xl text-blue-600"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-1">Croissance Garantie</h4>
                    <p className="text-sm text-gray-600">Flux régulier de projets pour augmenter votre CA</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Critères de sélection */}
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <i className="ri-checkbox-circle-line text-teal-600 mr-3"></i>
                Critères Requis
              </h3>
              <ul className="space-y-3">
                <li className="flex items-center text-sm">
                  <i className="ri-check-line text-blue-600 mr-3"></i>
                  <span className="text-gray-700">Statut auto-entrepreneur actif</span>
                </li>
                <li className="flex items-center text-sm">
                  <i className="ri-check-line text-blue-600 mr-3"></i>
                  <span className="text-gray-700">Minimum 2 ans d'expérience</span>
                </li>
                <li className="flex items-center text-sm">
                  <i className="ri-check-line text-blue-600 mr-3"></i>
                  <span className="text-gray-700">Assurances professionnelles à jour</span>
                </li>
                <li className="flex items-center text-sm">
                  <i className="ri-check-line text-blue-600 mr-3"></i>
                  <span className="text-gray-700">Références clients vérifiables</span>
                </li>
              </ul>
            </div>

            {/* Contact direct */}
            <div className="bg-gradient-to-r from-blue-600 to-teal-600 rounded-2xl p-8 text-white">
              <h3 className="text-xl font-bold mb-2">Une Question ?</h3>
              <p className="text-blue-100 mb-4 text-sm">
                Notre équipe est disponible pour vous accompagner
              </p>
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3 text-sm">
                  <i className="ri-phone-line"></i>
                  <a href="tel:+33745883392" className="hover:underline">07 45 88 33 92</a>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <i className="ri-mail-line"></i>
                  <a href="mailto:contact@leadsfenetres.fr" className="hover:underline">contact@leadsfenetres.fr</a>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <i className="ri-time-line"></i>
                  <span>Lun - Ven : 9h - 18h</span>
                </div>
              </div>
              <a
                href="https://wa.me/33745883392?text=Bonjour%2C%20je%20souhaite%20des%20informations%20sur%20votre%20réseau%20d'experts"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center bg-white text-blue-600 hover:bg-blue-50 font-semibold py-3 px-6 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line mr-2"></i>
                Nous Contacter sur WhatsApp
              </a>
            </div>
          </div>

          {/* Colonne droite : Formulaire de candidature */}
          <div className="bg-white rounded-2xl p-8 shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              Formulaire de Candidature
            </h3>
            
            <form id="recruitment-form" data-readdy-form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="nom" className="block text-sm font-semibold text-gray-700 mb-2">
                    Nom *
                  </label>
                  <input
                    type="text"
                    id="nom"
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>
                
                <div>
                  <label htmlFor="prenom" className="block text-sm font-semibold text-gray-700 mb-2">
                    Prénom *
                  </label>
                  <input
                    type="text"
                    id="prenom"
                    name="prenom"
                    value={formData.prenom}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="telephone" className="block text-sm font-semibold text-gray-700 mb-2">
                    Téléphone *
                  </label>
                  <input
                    type="tel"
                    id="telephone"
                    name="telephone"
                    value={formData.telephone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>
                
                <div>
                  <label htmlFor="ville" className="block text-sm font-semibold text-gray-700 mb-2">
                    Ville *
                  </label>
                  <input
                    type="text"
                    id="ville"
                    name="ville"
                    value={formData.ville}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="specialite" className="block text-sm font-semibold text-gray-700 mb-2">
                  Spécialité *
                </label>
                <select
                  id="specialite"
                  name="specialite"
                  value={formData.specialite}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm cursor-pointer"
                >
                  <option value="">Sélectionnez votre spécialité</option>
                  <option value="Menuiserie PVC">Menuiserie PVC</option>
                  <option value="Menuiserie Aluminium">Menuiserie Aluminium</option>
                  <option value="Menuiserie Bois">Menuiserie Bois</option>
                  <option value="Fenêtres">Fenêtres</option>
                  <option value="Volets">Volets</option>
                  <option value="Vérandas">Vérandas</option>
                  <option value="Baies vitrées">Baies vitrées</option>
                </select>
              </div>

              <div>
                <label htmlFor="experience" className="block text-sm font-semibold text-gray-700 mb-2">
                  Années d'Expérience *
                </label>
                <select
                  id="experience"
                  name="experience"
                  value={formData.experience}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm cursor-pointer"
                >
                  <option value="">Sélectionnez</option>
                  <option value="2-5 ans">2-5 ans</option>
                  <option value="5-10 ans">5-10 ans</option>
                  <option value="10+ ans">10+ ans</option>
                </select>
              </div>

              <div>
                <label htmlFor="motivations" className="block text-sm font-semibold text-gray-700 mb-2">
                  Motivations *
                </label>
                <textarea
                  id="motivations"
                  name="motivations"
                  value={formData.motivations}
                  onChange={handleChange}
                  required
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                  placeholder="Parlez-nous de vos motivations..."
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">Maximum 500 caractères</p>
              </div>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
                  <i className="ri-check-line text-green-600 text-xl"></i>
                  <p className="text-sm text-green-700">Merci ! Votre candidature a été envoyée. Nous vous contacterons rapidement.</p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
                  <i className="ri-error-warning-line text-red-600 text-xl"></i>
                  <p className="text-sm text-red-700">Une erreur est survenue. Veuillez réessayer.</p>
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-blue-600 to-teal-600 text-white px-6 py-4 rounded-lg text-base font-semibold hover:from-blue-700 hover:to-teal-700 transition-all flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line"></i>
                    Envoyer ma Candidature
                  </>
                )}
              </button>
            </form>
          </div>
        </div>

        {/* Bloc explicatif - Réparation de fenêtres */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border-l-4 border-blue-600">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="ri-tools-line text-3xl text-blue-600"></i>
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                La Réparation de Fenêtres : Un Marché en Pleine Croissance
              </h3>
              <p className="text-gray-600">
                Découvrez les opportunités de ce secteur porteur
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Types d'interventions */}
            <div>
              <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <i className="ri-hammer-line text-blue-600 mr-2"></i>
                Types d'Interventions
              </h4>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-blue-600 mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Remplacement de vitrage</strong>
                    <p className="text-sm text-gray-600">Double ou triple vitrage cassé ou embué</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-blue-600 mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Réparation de mécanismes</strong>
                    <p className="text-sm text-gray-600">Poignées, serrures et systèmes d'ouverture</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-blue-600 mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Rénovation de joints</strong>
                    <p className="text-sm text-gray-600">Amélioration de l'isolation</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Statistiques */}
            <div>
              <h4 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <i className="ri-bar-chart-line text-blue-600 mr-2"></i>
                Chiffres Clés du Marché
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-blue-600 mb-1">15M+</div>
                  <div className="text-xs text-gray-600">Fenêtres à rénover</div>
                </div>
                <div className="bg-teal-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-teal-600 mb-1">350€</div>
                  <div className="text-xs text-gray-600">Panier moyen</div>
                </div>
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-blue-600 mb-1">+12%</div>
                  <div className="text-xs text-gray-600">Croissance annuelle</div>
                </div>
                <div className="bg-teal-50 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-teal-600 mb-1">24h</div>
                  <div className="text-xs text-gray-600">Délai d'intervention</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RecruitmentSection;
